import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Scrollbars } from 'react-custom-scrollbars';
import classNames from 'classnames';
import { useStore } from 'effector-react';
import { VIP_URL } from '@constants';
import Modal from '@components/Modal';
import Title from '@components/Title';
import Button from '@components/Button';
import Input from '@components/Input';
import Textarea from '@components/Textarea';
import Slider from '@components/Slider';
import TextLink from '@components/TextLink';
import useInput from '@hooks/useInput';
import {
  Validator,
  ValidatorWrapper,
  type ValidatorWrapperHandlers,
} from '@components/Validator';
import useToggle from '@hooks/useToggle';
import { sendError } from '@components/Noti';
import { $isVip } from '@stores/memberStores';
import { showConfirmModal } from '@components/ConfirmModal';
import { $audioPlayer } from '@stores/audioPlayerStores';
import { TTSItem } from './TTSItem';
import { ttsConfig, type TTSConfigItem } from './ttsConfig';
import { TTSAvatar } from './TTSAvatar';
import { textToVoice } from '../utils/textToVoice';
import voiceToLib from '../utils/voiceToLib';

interface Props {
  albumId?: number;
  onRequestClose: () => void;
}

export function TTSModal({ albumId = 0, onRequestClose }: Props) {
  const [volume, setVolume] = useState<number>(50);
  const [speechRate, setSpeechRate] = useState<number>(0);
  const [groupIndex, setGroupIndex] = useState(0);
  const [selectedItem, setSelectedItem] = useState<TTSConfigItem>(
    ttsConfig[groupIndex].items[0]
  );
  const input = useInput<HTMLInputElement>();
  const textarea = useInput<HTMLTextAreaElement>();
  const validRef = useRef<ValidatorWrapperHandlers>(null);
  const ttsLoading = useToggle(false);
  const isVip = useStore($isVip);
  const [audioSrc, setAudioSrc] = useState('');
  const isPlaying = useStore($audioPlayer.$playingAudio) === audioSrc;

  const { items } = ttsConfig[groupIndex];

  useEffect(() => {
    $audioPlayer.stopAudio();
    setAudioSrc('');
  }, [speechRate, volume, textarea.value, input.value, selectedItem]);

  useEffect(() => {
    return () => {
      $audioPlayer.stopAudio();
    };
  }, []);

  const handleItemClick = useCallback(
    async (item: TTSConfigItem) => {
      if (!isVip && item.isVip) {
        const { confirmed } = await showConfirmModal({
          title: '温馨提示',
          content: (
            <div className="flex flex-col items-center">
              <div className="mb-10 text-xl">无法使用该配音</div>
              <div>该配音为个人专业版/企业版会员专享，升级即可使用！</div>
            </div>
          ),
          buttonText: '去升级',
        });
        if (confirmed) {
          window.open(VIP_URL, '_blank');
        }
        return;
      }
      setSelectedItem(item);
    },
    [setSelectedItem, isVip]
  );

  const handleReset = () => {
    setVolume(50);
    setSpeechRate(0);
    input.clear();
    textarea.clear();
  };

  const handleSave = async () => {
    if (!validRef.current?.validityCheck()) return;
    try {
      ttsLoading.on();
      const { url, subtitle } = await textToVoice({
        text: textarea.value,
        declaimer: selectedItem.voiceId,
        volume,
        speed: speechRate,
        sampleRate: selectedItem.sampleRate,
      }).catch(() => {
        throw new Error('语音合成失败，请重试');
      });
      await voiceToLib({ name: input.value.trim(), url, subtitle, albumId });
      onRequestClose();
    } catch (e) {
      if (e?.message) {
        sendError(e.message);
      }
    } finally {
      ttsLoading.off();
    }
  };

  const handleTry = async () => {
    if (!validRef.current?.validityCheck()) return;
    try {
      ttsLoading.on();
      const { url } = await textToVoice({
        text: textarea.value,
        declaimer: selectedItem.voiceId,
        volume,
        speed: speechRate,
        sampleRate: selectedItem.sampleRate,
      });
      $audioPlayer.playAudio(url, { loop: false });
      setAudioSrc(url);
    } catch (e) {
      sendError('音频合成出错，请重试');
    } finally {
      ttsLoading.off();
    }
  };

  const renderTryButton = () => {
    if (audioSrc) {
      return (
        <Button
          disabled={ttsLoading.isOn}
          onClick={() => $audioPlayer.playAudio(audioSrc, { loop: false })}
        >
          {isPlaying ? '暂停试听' : '试听音频'}
        </Button>
      );
    }
    return (
      <Button disabled={ttsLoading.isOn} onClick={handleTry}>
        {ttsLoading.isOn ? '正在合成' : '试听音频'}
      </Button>
    );
  };

  return (
    <Modal
      isOpen
      onRequestClose={onRequestClose}
      style={{ width: 1000, height: 'calc(100% - 60px)' }}
      header={<Title>语音合成</Title>}
      footer={
        <Button disabled={ttsLoading.isOn} onClick={handleSave}>
          保存音频
        </Button>
      }
      shouldCloseOnOverlayClick={false}
    >
      <ValidatorWrapper ref={validRef}>
        <div className="absolute inset-0 flex justify-between">
          <div style={{ width: 450 }} className="flex flex-col border-r">
            <div className="flex h-50 items-center bg-white-light">
              {ttsConfig.map((x, i) => (
                <div
                  key={x.id}
                  className={classNames('ml-20 cursor-pointer', {
                    'text-black-gray': groupIndex !== i,
                    'text-blue': groupIndex === i,
                  })}
                  onClick={() => {
                    if (ttsLoading.isOn) return;
                    setGroupIndex(i);
                  }}
                >
                  {x.name}
                </div>
              ))}
            </div>
            <div className="flex flex-1 pr-5">
              <Scrollbars>
                {items.map((x) => (
                  <TTSItem
                    key={x.voiceId}
                    data={x}
                    disabled={ttsLoading.isOn}
                    isSelected={selectedItem === x}
                    onClick={handleItemClick}
                  />
                ))}
              </Scrollbars>
            </div>
          </div>
          <div style={{ width: 500 }} className="flex flex-col">
            <div className="flex h-50 items-center bg-white-light px-15">
              <div>所选类型：</div>
              {selectedItem ? (
                <>
                  <TTSAvatar type={selectedItem.avatar} className="ml-10" />
                  <div className="ml-10">{selectedItem.name}</div>
                  <div className="ml-auto text-orange">{selectedItem.lang}</div>
                </>
              ) : (
                <div className="text-black-gray">
                  请选择一种声音类型进行合成
                </div>
              )}
            </div>
            <div
              style={{ maxHeight: 655 }}
              className="flex flex-1 flex-col px-15"
            >
              <Validator value={input.value} validFn={(val) => !!val.trim()}>
                {({ warning }) => (
                  <>
                    <div className="mt-20">
                      合成音频名称
                      {warning && (
                        <span className="ml-10 text-red">请输入内容</span>
                      )}
                    </div>
                    <Input
                      placeholder="请输入名称"
                      scheme="light"
                      className="mt-10 h-40"
                      maxLength={50}
                      disabled={ttsLoading.isOn}
                      warning={warning}
                      {...input.bind}
                    />
                  </>
                )}
              </Validator>
              <Validator value={textarea.value} validFn={(val) => !!val.trim()}>
                {({ warning }) => (
                  <>
                    <div className="mt-20">
                      合成音频内容
                      {warning && (
                        <span className="ml-10 text-red">请输入内容</span>
                      )}
                    </div>
                    <Textarea
                      placeholder="请输入要朗读的内容"
                      scheme="light"
                      className="mt-10 flex-1"
                      maxLength={2000}
                      disabled={ttsLoading.isOn}
                      warning={warning}
                      {...textarea.bind}
                    />
                  </>
                )}
              </Validator>
              <div className="mt-20 flex items-center justify-between">
                <div className="w-225">
                  <Slider
                    min={1}
                    max={100}
                    value={volume}
                    scheme="light"
                    valToString={(val) => `${val}%`}
                    disabled={ttsLoading.isOn}
                    onChange={setVolume}
                  >
                    <Slider.Header title="音量：" />
                    <Slider.Bar />
                  </Slider>
                </div>
                <div className="w-225">
                  <Slider
                    min={-500}
                    max={500}
                    value={speechRate}
                    scheme="light"
                    disabled={ttsLoading.isOn}
                    onChange={setSpeechRate}
                  >
                    <Slider.Header title="语速：" />
                    <Slider.Bar step={10} />
                  </Slider>
                </div>
              </div>
              <div className="mt-20 flex items-center justify-center">
                {renderTryButton()}
                <TextLink
                  className="ml-10"
                  scheme="light"
                  disabled={ttsLoading.isOn}
                  onClick={handleReset}
                >
                  重置内容
                </TextLink>
              </div>
            </div>
          </div>
        </div>
      </ValidatorWrapper>
    </Modal>
  );
}
